import { isLocale } from '@silospot/shared';
import { useQuery } from '@tanstack/react-query';
import { type ReactElement, useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AuthLayout } from './components/auth-layout';
import { Shell } from './components/cld/shell';
import { FullScreenLoader } from './components/loader';
import { ProtectedRoute } from './components/protected-route';
import { api } from './lib/api';
import { AudiencePage } from './pages/cld/audience';
import { BlocklistPage } from './pages/cld/blocklist';
import { CfConnectPage } from './pages/cld/cf-connect';
import { ConversionsPage } from './pages/cld/conversions';
import { FraudPage } from './pages/cld/fraud';
import { GadsManagePage } from './pages/cld/gads-manage';
import { GeoPage } from './pages/cld/geo';
import { GoogleAdsActionPage } from './pages/cld/google-ads-action';
import { GoogleAdsCampaignsPage } from './pages/cld/google-ads-campaigns';
import { GoogleAdsClaimsPage } from './pages/cld/google-ads-claims';
import { GoogleAdsRoiPage } from './pages/cld/google-ads-roi';
import { IpPage } from './pages/cld/ip';
import { LogPage } from './pages/cld/log';
import { OverviewPage } from './pages/cld/overview';
import { HomeRedirect } from './pages/cld/placeholder';
import { ProfilePage } from './pages/cld/profile';
import { RealtimePage } from './pages/cld/realtime';
import { ReviewPage } from './pages/cld/review';
import { SettingsPage } from './pages/cld/settings';
import { SupportPage } from './pages/cld/support';
import { VisitorPage } from './pages/cld/visitor';
import { ForgotPasswordPage } from './pages/forgot-password';
import { LoginPage } from './pages/login';
import { NotFoundPage } from './pages/not-found';
import { ResetPasswordPage } from './pages/reset-password';
import { SignupPage } from './pages/signup';
import { VerifyEmailPage } from './pages/verify-email';
import { useAuthStore } from './stores/auth-store';
import { useLocaleStore } from './stores/locale-store';

export function App(): ReactElement {
  const setUser = useAuthStore((state) => state.setUser);
  const setLocale = useLocaleStore((state) => state.setLocale);

  // Gate routing until auth is RESOLVED *and* propagated to the store — not just
  // until the query stops loading. Otherwise a hard-loaded deep link (e.g. the
  // Google Ads OAuth return on /projects/:id/settings) renders before setUser has
  // run, ProtectedRoute sees a null user, and bounces /login → /dashboard →
  // /projects/:id, dropping the requested path.
  const [authResolved, setAuthResolved] = useState(false);

  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: () => api.me(),
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  });

  useEffect(() => {
    if (meQuery.isSuccess) {
      setUser(meQuery.data.user);
      // The account language is authoritative across sessions/devices; adopt it
      // (localStorage is only a fast cache for the pre-auth screens).
      if (isLocale(meQuery.data.user.locale)) setLocale(meQuery.data.user.locale);
      setAuthResolved(true);
    } else if (meQuery.isError) {
      setUser(null);
      setAuthResolved(true);
    }
  }, [meQuery.isSuccess, meQuery.isError, meQuery.data, setUser, setLocale]);

  if (meQuery.isLoading || !authResolved) {
    return <FullScreenLoader />;
  }

  return (
    <Routes>
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
      </Route>

      {/* Authenticated but not yet verified: the 6-digit code page. */}
      <Route path="/verify-email" element={<VerifyEmailPage />} />

      <Route element={<ProtectedRoute />}>
        <Route element={<Shell />}>
          {/* CLD shell wraps the data-ready pages. The pages themselves are
              placeholders during A3 and get their real data-bound build in A4. */}
          <Route path="/dashboard" element={<HomeRedirect />} />
          <Route path="/projects/:id">
            <Route index element={<OverviewPage />} />
            <Route path="realtime" element={<RealtimePage />} />
            <Route path="audience" element={<AudiencePage />} />
            <Route path="conversions" element={<ConversionsPage />} />
            <Route path="geo" element={<GeoPage />} />
            <Route path="fraud" element={<FraudPage />} />
            <Route path="visits-ip" element={<IpPage />} />
            <Route path="blocklist" element={<BlocklistPage />} />
            <Route path="cloudflare" element={<CfConnectPage />} />
            <Route path="visitor/:visitId" element={<VisitorPage />} />
            <Route path="review" element={<ReviewPage />} />
            <Route path="log" element={<LogPage />} />
            <Route path="settings" element={<SettingsPage />} />
            {/* Advertising pages are project-scoped: the site comes from the URL,
                the single source of truth (no cross-site leak from a store). */}
            <Route path="ads/campaigns" element={<GoogleAdsCampaignsPage />} />
            <Route path="ads/manage" element={<GadsManagePage />} />
            <Route path="ads/action-center" element={<GoogleAdsActionPage />} />
            <Route path="ads/roi" element={<GoogleAdsRoiPage />} />
            <Route path="ads/claims" element={<GoogleAdsClaimsPage />} />
          </Route>
          <Route path="/settings/profile" element={<ProfilePage />} />
          <Route path="/support" element={<SupportPage />} />
        </Route>
      </Route>

      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
